/**
 * RGM Studio · Session workspace (multitrack DAW, mobile-first).
 *
 * Playback + recording run on one AudioContext clock (studioEngine).
 * Waveforms are DPR-sharp canvases drawn from decoded audio. Every clip
 * edit is non-destructive metadata and undoable via lane snapshots.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Undo2, Redo2, Magnet, ClipboardPaste, Mic, Plus } from "lucide-react";
import StudioToolsBar from "../components/studio/StudioToolsBar";
import VocalTrackDrawer from "../components/studio/VocalTrackDrawer";
import { DEFAULT_FX } from "../lib/studioFx";
import api from "@/lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { isSuspectDuration, probeMediaDurationMs, repairTakeDuration, StudioEngine, studioSupport, extForMime, PEAK_RATE_VIEW } from "../lib/studioEngine";
import { WaveformLane, laneColorTokens, InputMeter, formatSeconds } from "../components/studio/WaveformLane";
import { WaveformCanvas } from "../components/studio/WaveformCanvas";
import { VocalClip } from "../components/studio/VocalClip";
import MixerDrawer from "../components/studio/MixerDrawer";
import MasterDrawer from "../components/studio/MasterDrawer";
import LyricsDrawer from "../components/studio/LyricsDrawer";
import CollabDrawer from "../components/studio/CollabDrawer";
import ClipToolbar from "../components/studio/ClipToolbar";
import ClipToolsDrawer from "../components/studio/ClipToolsDrawer";
import FxDrawer from "../components/studio/FxDrawer";
import TrimHandle from "../components/studio/TrimHandle";
import { laneSnapshot, rangeLaneEntries, punchInEntries } from "../lib/studioEdits";
import { BeatVolumeSlider } from "../components/studio/BeatVolumeSlider";
import { LyricsPanel } from "../components/studio/LyricsPanel";
import { MasterMeter } from "../components/studio/MasterMeter";

const BEAT_PROXY_LIMIT = 25 * 1024 * 1024;
const BEAT_HARD_CAP    = 100 * 1024 * 1024;
const BEAT_ACCEPT = "audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/wave,audio/mp4,audio/x-m4a,audio/m4a,audio/aac,audio/webm,audio/ogg";
const HISTORY_LIMIT = 30;
const BASE_PX_PER_SEC = 30; // timeline scale at 100% zoom
const ZOOMS = [0.5, 0.75, 1, 1.5, 2, 3];
const MAX_VOCALS = 20;
const readPref = (k, d) => { try { const v = localStorage.getItem(k); return v == null ? d : JSON.parse(v); } catch { return d; } };
const writePref = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* */ } };
const MIN_SESSION_MS = 60000;
const LANE_HEIGHT = 56;
const LIVE_RATE = 20;      // live recording waveform buckets / sec

function humanBytes(b) {
  if (b >= 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  if (b >= 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${b} B`;
}

export default function StudioSessionPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const support = useMemo(() => studioSupport(), []);
  const [project, setProject] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [takes, setTakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [purchases, setPurchases] = useState(null);
  const [showBeatPicker, setShowBeatPicker] = useState(false);
  const [showVocals, setShowVocals] = useState(false);
  const [freeBeats, setFreeBeats] = useState(null);
  const [studioPro, setStudioPro] = useState(false);
  const [access, setAccess] = useState(null);
  const [zoomIdx, setZoomIdx] = useState(2);
  const PX_PER_SEC = BASE_PX_PER_SEC * ZOOMS[zoomIdx];
  const [monitorOn, setMonitorOn] = useState(() => !!readPref("rgm_studio_monitor", false));
  const [monitorVol, setMonitorVol] = useState(() => Number(readPref("rgm_studio_monitor_vol", 0.8)));
  const [metro, setMetro] = useState({ on: false, bpm: 90 });
  const metroBpm = metro.bpm;
  const [masterVolume, setMasterVolume] = useState(1);
  const [clipboard, setClipboard] = useState(null);
  const [members, setMembers] = useState({});
  const isOwner = access?.role !== "collaborator";
  const me = access?.user_id;
  const takeAuthor = (k) => k.created_by || k.recorded_by || k.user_id;
  const canWrite = (trackId) => {
    if (isOwner) return true;
    if ((access?.writable_track_ids || []).includes(trackId)) return true;
    const t = tracks.find((x) => x.track_id === trackId);
    return !!t && (t.created_by || t.user_id) === me;
  };
  const canEditTake = (k) => isOwner || (takeAuthor(k) === me && canWrite(k.track_id));
  const canWriteRef = useRef(canWrite);
  canWriteRef.current = canWrite;
  const [previewLeft, setPreviewLeft] = useState(0);
  const previewTimer = useRef(null);
  const [engineState, setEngineState] = useState("idle");
  const [playheadMs, setPlayheadMs] = useState(0);
  const [selectedTrackId, setSelectedTrackId] = useState(null);
  const [beatView, setBeatView] = useState(null); // {peaks, rate, duration_ms}
  const [clipPeaks, setClipPeaks] = useState({}); // url -> {peaks, rate}
  const [beatVolume, setBeatVolume] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [beatBroken, setBeatBroken] = useState(null);
  const [showMixer, setShowMixer] = useState(false);
  const [showLyrics, setShowLyrics] = useState(false);
  const [lyricsPanel, setLyricsPanel] = useState(() => {
    try { return localStorage.getItem("rgm_studio_lyrics_panel") !== "0"; } catch { return true; }
  });
  const toggleLyricsPanel = () => setLyricsPanel((v) => {
    try { localStorage.setItem("rgm_studio_lyrics_panel", v ? "0" : "1"); } catch { /* */ }
    return !v;
  });
  const [showCollab, setShowCollab] = useState(false);
  const [showTools, setShowTools] = useState(false);
  const [showFx, setShowFx] = useState(false);
  const [showMaster, setShowMaster] = useState(false);
  const [master, setMaster] = useState({ preset: "off", intensity: 50 });
  const [masterSaving, setMasterSaving] = useState(false);
  const masterSaveTimer = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [lyrics, setLyrics] = useState("");
  const [micLevel, setMicLevel] = useState(0);
  const [selection, setSelection] = useState(null);
  const [dragPreview, setDragPreview] = useState(null); // beat trim preview
  const [clipPreview, setClipPreview] = useState(null); // {key, partial}
  const [rangeMode, setRangeMode] = useState(false);
  const [range, setRange] = useState(null); // {takeId, trackId, startMs, endMs} timeline ms
  const [snap, setSnap] = useState(false);
  const [pendingTake, setPendingTake] = useState(null);
  const [liveVersion, setLiveVersion] = useState(0);
  const liveRef = useRef({ peaks: [], startMs: 0, trackId: null });
  const engineRef = useRef(null);
  const scrollRef = useRef(null);
  const playheadLineRef = useRef(null);
  const lastStateTick = useRef(0);
  const historyRef = useRef({ undo: [], redo: [] });
  const takesRef = useRef([]);
  const requestedPeaks = useRef(new Set());
  const [, setHistoryTick] = useState(0);
  takesRef.current = takes;

  const reload = useCallback(async () => {
    try {
      const r = await api.get(`/studio/projects/${sessionId}`);
      setProject(r.data.project);
      setTitle(r.data.project.title || "");
      setLyrics(r.data.project.lyrics || "");
      if (r.data.project.master) setMaster(r.data.project.master);
      if (typeof r.data.project.beat_volume === "number") setBeatVolume(r.data.project.beat_volume);
      setTracks(r.data.tracks || []);
      setStudioPro(!!r.data.studio_pro);
      setAccess(r.data.access || null);
      setMasterVolume(typeof r.data.project?.master_volume === "number" ? r.data.project.master_volume : 1);
      setMetro((m) => (m.touched ? m : { ...m, bpm: Math.round(Number(r.data.project?.beat_ref?.bpm || r.data.project?.bpm) || 90) }));
      setMembers(r.data.members || {});
      setTakes(r.data.takes || []);
      // Playback-only repair of takes saved with a wrong (seconds) length.
      (r.data.takes || []).filter(isSuspectDuration).forEach(async (k) => {
        const real = await probeMediaDurationMs(k.clip_url);
        if (real > 0) setTakes((list) => list.map((x) => (x.take_id === k.take_id ? repairTakeDuration(x, real) : x)));
      });
      const firstTrack = r.data.access?.role === "collaborator"
        ? (r.data.tracks || []).find((t) => (r.data.access.writable_track_ids || []).includes(t.track_id))
        : (r.data.tracks || [])[0];
      setSelectedTrackId((cur) => cur || firstTrack?.track_id || null);
      return r.data;
    } catch {
      toast.error("Could not open this Studio session");
      navigate("/studio");
      return null;
    } finally { setLoading(false); }
  }, [sessionId, navigate]);

  useEffect(() => { reload(); }, [sessionId]); // eslint-disable-line

  useEffect(() => () => { try { engineRef.current?.dispose(); } catch { /* */ } }, []);

  const beatUrl = project && (project.beat_source === "marketplace"
    ? project.beat_ref?.audio_url : project.beat_ref?.upload_url);
  const beatRef = useMemo(() => project?.beat_ref || {}, [project]);

  // One engine per beat. The previous engine is fully disposed so no
  // duplicate audio graphs / contexts can play at the same time.
  useEffect(() => {
    if (!project) return;
    const prev = engineRef.current;
    if (prev && prev.beatUrl === beatUrl) return;
    if (prev) { prev.onStateChange(null); prev.dispose(); }
    const eng = new StudioEngine(beatUrl);
    eng.onStateChange(setEngineState);
    engineRef.current = eng;
    requestedPeaks.current = new Set();
    setClipPeaks({});
    setBeatView(null);
    if (beatUrl) {
      eng.beatPeaks(PEAK_RATE_VIEW).then((v) => {
        if (engineRef.current !== eng) return;
        setBeatView(v);
        setBeatBroken(v && v.duration_ms > 0 ? null : { reason: "unreadable" });
      }).catch((e) => { if (engineRef.current === eng) setBeatBroken({ reason: e?.message || "decode_failed" }); });
    }
  }, [beatUrl, project]);

  // Decode each take once → sharp waveform + instant playback.
  useEffect(() => {
    const eng = engineRef.current;
    if (!eng) return;
    for (const k of takes) {
      const url = k.clip_url;
      if (!url || requestedPeaks.current.has(url)) continue;
      requestedPeaks.current.add(url);
      eng.clipPeaks(url, PEAK_RATE_VIEW).then((v) => {
        if (v && engineRef.current === eng) setClipPeaks((m) => ({ ...m, [url]: v }));
      });
    }
  }, [takes, beatUrl]);

  // Live mixer: volume / pan / mute / solo apply immediately.
  useEffect(() => { engineRef.current?.setTrackMix(tracks); }, [tracks, engineState]);
  useEffect(() => { engineRef.current?.setBeatVolume(beatVolume); }, [beatVolume, engineState]);

  const ensureEngineReady = useCallback(async () => {
    const eng = engineRef.current;
    if (!eng || !beatUrl) throw new Error("attach a beat first");
    await eng.ensureReady();
    eng.setMaster(master.preset, master.intensity);
    eng.setFxAllowed(studioPro || previewLeft > 0 || !isOwner);
    eng.setTrackMix(tracks);
    eng.setBeatVolume(beatVolume);
    eng.setMasterVolume(masterVolume);
    eng.setMonitorVolume(monitorVol);
    eng.setMetronome(metro);
    return eng;
  }, [beatUrl, master, tracks, beatVolume, studioPro, previewLeft, isOwner, masterVolume, monitorVol, metro]);

  const toggleMonitor = (on) => {
    setMonitorOn(on); writePref("rgm_studio_monitor", on);
    engineRef.current?.setMonitor(on);
    if (on) toast.message("🎧 Monitor on — use wired headphones for the lowest delay. Bluetooth adds noticeable delay.", { duration: 5000 });
  };
  const changeMonitorVol = (v) => { setMonitorVol(v); writePref("rgm_studio_monitor_vol", v); engineRef.current?.setMonitorVolume(v); };
  const changeMetro = (patch) => setMetro((m) => {
    const next = { ...m, ...patch, touched: true };
    engineRef.current?.setMetronome(next);
    return next;
  });
  const masterVolTimer = useRef(null);
  const beatVolTimer = useRef(null);
  const changeMasterVolume = (v) => {
    setMasterVolume(v);
    engineRef.current?.setMasterVolume(v);
    if (!isOwner) return;
    clearTimeout(masterVolTimer.current);
    masterVolTimer.current = setTimeout(() => {
      api.patch(`/studio/projects/${sessionId}`, { master_volume: v }).catch(() => toast.error("Could not save master volume"));
    }, 500);
  };
  const zoomBy = (dir) => setZoomIdx((i) => Math.max(0, Math.min(ZOOMS.length - 1, i + dir)));

  const changeMaster = (next) => {
    setMaster(next);
    engineRef.current?.setMaster(next.preset, next.intensity);
    clearTimeout(masterSaveTimer.current);
    setMasterSaving(true);
    masterSaveTimer.current = setTimeout(async () => {
      try { await api.patch(`/studio/projects/${sessionId}`, { master: next }); }
      catch { toast.error("Could not save mastering"); }
      finally { setMasterSaving(false); }
    }, 500);
  };

  const [showBeatVol, setShowBeatVol] = useState(false);
  const saveBeatVolume = async (next) => {
    try { await api.patch(`/studio/projects/${sessionId}`, { beat_volume: next }); }
    catch { toast.error("Could not save beat volume"); }
  };

  // ─── Session duration (px) ─────────────────────────────────────
  const beatDurationMs = beatView?.duration_ms || beatRef.duration_ms || 0;
  const latestClipEndMs = useMemo(() => {
    let mx = 0;
    for (const k of takes) {
      const win = (k.clip_end_ms || k.duration_ms || 0) - (k.clip_start_ms || 0);
      mx = Math.max(mx, (k.beat_offset_ms || 0) + Math.max(0, win));
    }
    return mx;
  }, [takes]);
  const beatEndMs = Math.max(beatRef.clip_end_ms || 0, beatDurationMs || 0);
  const liveEndMs = engineState === "recording" ? playheadMs : 0;
  const sessionDurationMs = Math.max(MIN_SESSION_MS, beatEndMs, latestClipEndMs + 4000, liveEndMs + 8000);
  const totalPx = Math.ceil((sessionDurationMs / 1000) * PX_PER_SEC);
  const bpm = Number(beatRef.bpm || project?.bpm) || 0;
  const sessionBpm = Math.max(40, Math.min(240, metroBpm || bpm || 90));
  const gridMs = 60000 / sessionBpm;

  // Playhead: DOM-updated every frame, React state ~12×/s (cheap renders).
  const ppsRef = useRef(PX_PER_SEC);
  ppsRef.current = PX_PER_SEC;
  const moveHead = useCallback((ms) => {
    const px = (ms / 1000) * ppsRef.current;
    if (playheadLineRef.current) playheadLineRef.current.style.transform = `translateX(${px}px)`;
    const el = scrollRef.current;
    if (el) {
      const vw = el.clientWidth || 1;
      if (px < el.scrollLeft + 16 || px > el.scrollLeft + vw * 0.75) el.scrollLeft = Math.max(0, px - vw * 0.3);
    }
    const now = performance.now();
    if (now - lastStateTick.current > 80) { lastStateTick.current = now; setPlayheadMs(Math.round(ms)); }
  }, []);
  useEffect(() => {
    if (playheadLineRef.current) playheadLineRef.current.style.transform = `translateX(${(playheadMs / 1000) * PX_PER_SEC}px)`;
  }, [playheadMs, PX_PER_SEC]);

  // ─── History helpers ───────────────────────────────────────────
  const pushHistory = useCallback((action) => {
    const h = historyRef.current;
    h.undo.push(action);
    if (h.undo.length > HISTORY_LIMIT) h.undo.shift();
    h.redo = [];
    setHistoryTick((v) => v + 1);
  }, []);
  const runUndo = useCallback(async () => {
    const h = historyRef.current;
    const action = h.undo.pop();
    if (!action) return;
    try { await action.undo(); h.redo.push(action); toast.message(`Undid ${action.label}`); }
    catch { toast.error("Undo failed"); h.undo.push(action); }
    setSelection(null); setRange(null);
    setHistoryTick((v) => v + 1);
  }, []);
  const runRedo = useCallback(async () => {
    const h = historyRef.current;
    const action = h.redo.pop();
    if (!action) return;
    try { await action.redo(); h.undo.push(action); toast.message(`Redid ${action.label}`); }
    catch { toast.error("Redo failed"); h.redo.push(action); }
    setSelection(null); setRange(null);
    setHistoryTick((v) => v + 1);
  }, []);

  const restoreLane = useCallback(async (trackId, snap) => {
    await api.put(`/studio/projects/${sessionId}/tracks/${trackId}/takes-state`, { takes: snap });
    await reload();
  }, [sessionId, reload]);

  // Every lane edit: snapshot before → run → snapshot after → undoable.
  const laneEdit = useCallback(async (trackId, label, op) => {
    const before = laneSnapshot(takesRef.current, trackId);
    try {
      const res = await op();
      const data = await reload();
      const after = laneSnapshot(data?.takes || [], trackId);
      pushHistory({ label, undo: () => restoreLane(trackId, before), redo: () => restoreLane(trackId, after) });
      return res;
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not ${label}`);
      return null;
    }
  }, [reload, pushHistory, restoreLane]);

  // ─── Beat picker + upload (unchanged from 1C) ──────────────────
  const openBeatPicker = async () => {
    setShowBeatPicker(true);
    if (freeBeats === null) {
      api.get("/studio/beats/free").then((r) => setFreeBeats(r.data.beats || [])).catch(() => setFreeBeats([]));
    }
    if (purchases === null) {
      try {
        const r = await api.get("/beats/me/purchases");
        setPurchases(r.data.purchases || []);
      } catch { setPurchases([]); }
    }
  };
  const attachMarketplaceBeat = async (beatId) => {
    try {
      await api.post(`/studio/projects/${sessionId}/beat/marketplace`, { beat_id: beatId });
      toast.success("Beat attached"); setShowBeatPicker(false); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not attach beat"); }
  };
  const uploadBeatFile = async (file) => {
    if (!file) return;
    if (file.size > BEAT_HARD_CAP) { toast.error(`Beat too large — max ${humanBytes(BEAT_HARD_CAP)}.`); return; }
    setUploading(true);
    setUploadProgress({ pct: 0, note: "Uploading…" });
    try {
      let beatUrlPreview = null;
      if (file.size <= BEAT_PROXY_LIMIT) {
        const fd = new FormData();
        fd.append("file", file); fd.append("duration_ms", "0");
        const r = await api.post(`/studio/projects/${sessionId}/beat/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => e.total && setUploadProgress({ pct: Math.round((e.loaded / e.total) * 90), note: "Uploading…" }),
        });
        beatUrlPreview = r.data?.beat_ref?.upload_url || null;
      } else {
        setUploadProgress({ pct: 0, note: "Preparing secure upload…" });
        const sig = await api.post(`/studio/projects/${sessionId}/beat/upload-signature`);
        if (file.size > (sig.data?.max_bytes || BEAT_HARD_CAP)) throw new Error(`Beat exceeds the ${humanBytes(sig.data?.max_bytes || BEAT_HARD_CAP)} cap`);
        const fd = new FormData();
        Object.entries(sig.data.params).forEach(([k, v]) => fd.append(k, String(v)));
        fd.append("file", file);
        const cdn = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open("POST", sig.data.upload_url, true);
          xhr.upload.onprogress = (e) => e.lengthComputable && setUploadProgress({ pct: Math.round((e.loaded / e.total) * 80), note: "Uploading to Cloudinary…" });
          xhr.onload = () => { try { const body = JSON.parse(xhr.responseText || "{}"); if (xhr.status >= 200 && xhr.status < 300) resolve(body); else reject(new Error(body?.error?.message || `Cloudinary ${xhr.status}`)); } catch (err) { reject(err); } };
          xhr.onerror = () => reject(new Error("network error uploading to Cloudinary"));
          xhr.send(fd);
        });
        setUploadProgress({ pct: 85, note: "Verifying with server…" });
        const fin = await api.post(`/studio/projects/${sessionId}/beat/upload-finalize`, {
          public_id: cdn.public_id, secure_url: cdn.secure_url, bytes: cdn.bytes,
          duration_ms: Math.round((cdn.duration || 0) * 1000),
          mime: file.type || cdn.resource_type || "audio/mpeg",
        });
        beatUrlPreview = fin.data?.beat_ref?.upload_url || cdn.secure_url;
      }
      setUploadProgress({ pct: 95, note: "Processing audio…" });
      const probeEng = new StudioEngine(beatUrlPreview);
      const probe = await probeEng.verifyBeatPlayable(beatUrlPreview);
      probeEng.dispose();
      if (!probe.ok) {
        try { await api.delete(`/studio/projects/${sessionId}/beat`); } catch { /* */ }
        toast.error(`This beat could not be processed — ${probe.reason || "unknown"}.`);
        setBeatBroken({ reason: probe.reason || "unknown" }); reload(); return;
      }
      setUploadProgress({ pct: 100, note: "Ready" });
      toast.success(`Beat ready · ${Math.round((probe.duration_ms || 0) / 1000)}s`);
      setBeatBroken(null); setShowBeatPicker(false); reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Beat upload failed");
    } finally { setUploading(false); setTimeout(() => setUploadProgress(null), 900); }
  };


  const [addingVocal, setAddingVocal] = useState(false);
  const addVocalTrack = async (role = "lead", label) => {
    if (tracks.length >= MAX_VOCALS) { toast.error(`Max ${MAX_VOCALS} vocal tracks per session`); return; }
    setAddingVocal(true);
    try {
      const r = await api.post(`/studio/projects/${sessionId}/tracks`, { role, label });
      const t = r.data.track;
      setTracks((prev) => [...prev, t]);
      setSelectedTrackId(t.track_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add vocal"); }
    finally { setAddingVocal(false); }
  };

  const patchTrack = useCallback(async (trackId, partial) => {
    setTracks((prev) => prev.map((t) => (t.track_id === trackId ? { ...t, ...partial } : t)));
    if (typeof partial.monitor === "boolean") { try { await engineRef.current?.setMonitor(partial.monitor); } catch { /* */ } }
    if (!canWriteRef.current(trackId)) return;
    try { await api.patch(`/studio/projects/${sessionId}/tracks/${trackId}`, partial); }
    catch { toast.error("Could not save track change"); reload(); }
  }, [sessionId, reload]);

  const beatClip = useMemo(() => ({
    clip_start_ms: beatRef.clip_start_ms || 0,
    clip_end_ms: beatRef.clip_end_ms || 0,
    clip_gain_db: beatRef.clip_gain_db || 0,
    fade_in_ms: beatRef.fade_in_ms || 0,
    fade_out_ms: beatRef.fade_out_ms || 0,
  }), [beatRef]);

  // ─── Recording ────────────────────────────────────────────────
  const onLiveLevel = useCallback((peak, posMs) => {
    const L = liveRef.current;
    const idx = Math.floor(((posMs - L.startMs) / 1000) * LIVE_RATE);
    if (idx < 0) return;
    const grew = idx >= L.peaks.length;
    while (L.peaks.length <= idx) L.peaks.push(0);
    if (peak > L.peaks[idx]) L.peaks[idx] = peak;
    if (grew) setLiveVersion(L.peaks.length);
  }, []);

  const doRecord = async () => {
    if (!support.supported) { toast.error("Recording not supported in this browser"); return; }
    if (!project?.beat_source) { toast.error("Attach a beat first"); return; }
    if (beatBroken && !beatView) { toast.error("This beat could not be processed. Please re-upload."); return; }
    let trackId = selectedTrackId && canWrite(selectedTrackId) ? selectedTrackId : null;
    let localTracks = tracks;
    if (!trackId) {
      trackId = localTracks.find((t) => canWrite(t.track_id))?.track_id || null;
      if (!trackId) {
        if (localTracks.length >= MAX_VOCALS) { toast.error(isOwner ? `Max ${MAX_VOCALS} vocal tracks per session` : "No free vocal lane — ask the owner to assign you one"); return; }
        try {
          const r = await api.post(`/studio/projects/${sessionId}/tracks`, { role: "lead" });
          trackId = r.data.track.track_id;
          localTracks = [...localTracks, r.data.track];
          setTracks(localTracks);
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not create a vocal lane"); return; }
      }
      if (selectedTrackId && !isOwner) toast.message("Recording on your own vocal lane");
      setSelectedTrackId(trackId);
    }
    const monitor = monitorOn;
    showHeadphonesTipOnce(); setMicLevel(0); setSelection(null); setRange(null);
    // Punch-in: recording starts exactly at the selected playhead.
    const startAtMs = Math.max(0, Math.round(playheadMs || 0));
    liveRef.current = { peaks: [], startMs: startAtMs, trackId };
    setLiveVersion(0);
    try {
      const eng = await ensureEngineReady();
      await eng.startRecording({
        mime: support.mime || undefined, monitor, startAtMs,
        mix: { takes, beatClip, excludeTrackId: trackId },
        onTick: (s) => moveHead(s * 1000),
        onLevel: (peak, posMs) => { setMicLevel(peak); onLiveLevel(peak, posMs); },
        onError: (e) => toast.error(`Recorder error: ${e?.message || e}`),
      });
      const lat = monitor ? eng.monitorLatencyMs() : 0;
      if (lat > 45) toast.message(`🎧 Monitor delay about ${lat} ms on this device. Wired headphones are fastest — or turn Monitor off. Your take still lands on the beat.`, { duration: 7000 });
    } catch (e) { toast.error(e?.message || "Could not start recording"); }
  };

  // (take save pipeline lives below: uploadTake + doStopRecord)

  // Take upload: direct to storage (bypasses the edge proxy timeout),
  // idempotent client_take_id, automatic retries, shown immediately.
  const uploadTake = useCallback(async (trackId, job) => {
    const meta = { client_take_id: job.cid, mime: job.mime, duration_ms: job.durationMs, beat_offset_ms: job.timing.beat_offset_ms,
      lead_in_ms: job.timing.lead_in_ms, ctx_start_ms: job.timing.ctx_start_ms, peaks: job.peaks.join(",") };
    const base = `/studio/projects/${sessionId}/tracks/${trackId}/takes`;
    let lastErr;
    for (let attempt = 0; attempt < 4; attempt++) {
      try {
        let sig = null;
        try { sig = (await api.post(`${base}/upload-signature`, { client_take_id: job.cid })).data; }
        catch (e) { if (e?.response?.status !== 503) throw e; }
        if (sig) {
          const fd = new FormData();
          Object.entries(sig.params).forEach(([k, v]) => fd.append(k, String(v)));
          fd.append("file", new File([job.blob], `take.${extForMime(job.mime)}`, { type: job.mime }));
          const res = await fetch(sig.upload_url, { method: "POST", body: fd });
          if (!res.ok) throw new Error("storage upload failed");
          return (await api.post(`${base}/upload-finalize`, meta)).data.take;
        }
        const fd = new FormData();
        fd.append("file", new File([job.blob], `take.${extForMime(job.mime)}`, { type: job.mime }));
        Object.entries(meta).forEach(([k, v]) => fd.append(k, String(v ?? "")));
        return (await api.post(base, fd, { headers: { "Content-Type": "multipart/form-data" } })).data.take;
      } catch (e) {
        lastErr = e;
        const code = e?.response?.status;
        if (code && code >= 400 && code < 500 && code !== 408 && code !== 429) throw e;
        await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
      }
    }
    throw lastErr;
  }, [sessionId]);

  const doStopRecord = async () => {
    setUploading(true);
    const L = liveRef.current;
    const trackId = L.trackId || selectedTrackId;
    const before = laneSnapshot(takesRef.current, trackId);
    try {
      const eng = engineRef.current;
      const out = await eng?.stopRecording();
      if (!out) return;
      setPlayheadMs(eng.positionMs());
      // Keep the recorded waveform on screen while the take uploads.
      setPendingTake({ trackId, startMs: out.timing.beat_offset_ms, peaks: L.peaks.slice(), rate: LIVE_RATE });
      // Peak decode never blocks saving for more than 3 s.
      const pk = await Promise.race([eng.peakify(out.blob), new Promise((r) => setTimeout(() => r(null), 3000))]);
      const leadBuckets = Math.round((out.timing.lead_in_ms / 1000) * LIVE_RATE);
      const job = {
        cid: (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`).replace(/[^A-Za-z0-9_-]/g, ""),
        blob: out.blob, mime: out.mime, timing: out.timing,
        durationMs: pk?.duration_ms > 0 ? pk.duration_ms : out.timing.duration_ms,
        peaks: pk?.peaks || [...new Array(leadBuckets).fill(0), ...L.peaks],
      };
      const newTake = await uploadTake(trackId, job);
      if (pk?.buffer) {
        eng.cacheBuffer(newTake.clip_url, pk.buffer);
        requestedPeaks.current.add(newTake.clip_url);
        setClipPeaks((m) => ({ ...m, [newTake.clip_url]: { peaks: pk.viewPeaks, rate: PEAK_RATE_VIEW } }));
      }
      setTakes((list) => (list.some((k) => k.take_id === newTake.take_id) ? list : [...list, newTake]));
      setSelection({ kind: "vocal", trackId, takeId: newTake.take_id });
      let after = [...before.filter((x) => x.take_id !== newTake.take_id), ...laneSnapshot([newTake], trackId)];
      // Punch-in: the new take replaces that section of earlier takes on this lane.
      const nStart = newTake.beat_offset_ms || 0;
      const nEnd = nStart + ((newTake.clip_end_ms ?? newTake.duration_ms) - (newTake.clip_start_ms || 0));
      const mine = new Set(takesRef.current.filter((k) => takeAuthor(k) === me || (!k.created_by && isOwner)).map((k) => k.take_id));
      const punched = punchInEntries([...takesRef.current.filter((k) => k.take_id !== newTake.take_id), newTake], trackId, newTake.take_id, nStart, nEnd, (id) => mine.has(id));
      if (punched) {
        try {
          await api.put(`/studio/projects/${sessionId}/tracks/${trackId}/takes-state`, { takes: punched });
          const data = await reload();
          after = laneSnapshot(data?.takes || [], trackId);
        } catch { toast.error("Saved the take, but couldn't replace the old section — use Undo or trim manually"); }
      }
      pushHistory({ label: "record", undo: () => restoreLane(trackId, before), redo: () => restoreLane(trackId, after) });
      toast.success(`Take saved · ${(Math.max(0, (newTake.clip_end_ms || 0) - (newTake.clip_start_ms || 0)) / 1000).toFixed(1)}s`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save take — checking what was saved…");
    } finally {
      setPendingTake(null); setMicLevel(0); setUploading(false);
      reload(); // always reconcile with the server — no hidden or duplicate takes
    }
  };

  const showHeadphonesTipOnce = () => {
    try {
      if (localStorage.getItem("rgm_studio_hp_tip")) return;
      localStorage.setItem("rgm_studio_hp_tip", "1");
    } catch { return; }
    toast.message("🎧 Tip: wear headphones so the beat doesn't bleed into your mic. Keep RGM open while recording.", { duration: 6000 });
  };

  // ─── RGM Studio Pro FX ─────────────────────────────────────────
  const fxOn = studioPro || previewLeft > 0;
  useEffect(() => { engineRef.current?.setFxAllowed(fxOn || !isOwner); }, [fxOn, isOwner, engineState]);
  const fxChange = useCallback((trackId, fx) => {
    setTracks((prev) => prev.map((t) => (t.track_id === trackId ? { ...t, fx } : t)));
  }, []);
  const fxCommit = useCallback(async (trackId, fx) => {
    // Free members save the noise gate only; Pro modules stay preview-only.
    const body = studioPro ? fx : { ...DEFAULT_FX, gate: fx.gate || DEFAULT_FX.gate };
    try { await api.patch(`/studio/projects/${sessionId}/tracks/${trackId}`, { fx: body }); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Could not save FX"); reload(); }
  }, [studioPro, sessionId, reload]);
  const startFxPreview = () => {
    clearInterval(previewTimer.current);
    const until = Date.now() + 15000;
    setPreviewLeft(15);
    if (engineRef.current?.state !== "playing") doPlay();
    previewTimer.current = setInterval(() => {
      const left = Math.max(0, Math.ceil((until - Date.now()) / 1000));
      setPreviewLeft(left);
      if (left <= 0) {
        clearInterval(previewTimer.current);
        reload(); // drop unsaved preview settings
        toast.message("Pro preview ended", { description: "Upgrade to RGM Premium to save and export vocal FX.", action: { label: "Upgrade", onClick: () => navigate("/membership") } });
      }
    }, 250);
  };
  useEffect(() => () => clearInterval(previewTimer.current), []);

  const saveLyrics = useCallback(async (next) => {
    try { await api.patch(`/studio/projects/${sessionId}`, { lyrics: next }); setProject((p) => (p ? { ...p, lyrics: next } : p)); }
    catch { toast.error("Could not save lyrics"); }
  }, [sessionId]);

  // ─── Clip editing (all undoable) ───────────────────────────────
  const takeUrl = (trackId, takeId) => `/studio/projects/${sessionId}/tracks/${trackId}/takes/${takeId}`;
  const selectedTakeObj = selection?.kind === "vocal" ? takes.find((k) => k.take_id === selection.takeId) : null;

  const patchTake = useCallback((take, partial, label) => (
    laneEdit(take.track_id, label, () => api.patch(takeUrl(take.track_id, take.take_id), partial))
  ), [laneEdit]); // eslint-disable-line

  const patchBeatRaw = useCallback(async (partial) => {
    await api.patch(`/studio/projects/${sessionId}/beat`, partial);
  }, [sessionId]);

  const editBeatClip = useCallback(async (partial, label = "beat edit") => {
    const before = Object.fromEntries(Object.keys(partial).map((k) => [k, beatRef[k] || 0]));
    try {
      await patchBeatRaw(partial);
      pushHistory({ label, undo: () => patchBeatRaw(before).then(reload), redo: () => patchBeatRaw(partial).then(reload) });
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update beat clip"); }
  }, [beatRef, patchBeatRaw, pushHistory, reload]);

  const splitSelectedTake = useCallback(async () => {
    const take = selectedTakeObj;
    if (!take) return;
    const local = Math.round(playheadMs - (take.beat_offset_ms || 0) + (take.clip_start_ms || 0));
    if (local <= (take.clip_start_ms || 0) || local >= (take.clip_end_ms || take.duration_ms || 0)) { toast.error("Tap the clip where you want to cut first"); return; }
    const r = await laneEdit(take.track_id, "split", () => api.post(`${takeUrl(take.track_id, take.take_id)}/split`, { at_ms: local }));
    if (r) { toast.success("Clip split"); setSelection({ kind: "vocal", trackId: take.track_id, takeId: r.data?.right?.take_id }); }
  }, [selectedTakeObj, playheadMs, laneEdit]); // eslint-disable-line

  const duplicateSelectedTake = useCallback(async () => {
    const take = selectedTakeObj;
    if (!take) return;
    const r = await laneEdit(take.track_id, "duplicate", () => api.post(`${takeUrl(take.track_id, take.take_id)}/duplicate`, {}));
    if (r) { toast.success("Clip duplicated"); setSelection({ kind: "vocal", trackId: take.track_id, takeId: r.data?.take?.take_id }); }
  }, [selectedTakeObj, laneEdit]); // eslint-disable-line

  const copySelectedTake = () => {
    if (!selectedTakeObj) return;
    setClipboard(selectedTakeObj);
    toast.success("Clip copied — tap Paste");
  };
  const takeSpan = (k) => [k.beat_offset_ms || 0, (k.beat_offset_ms || 0) + ((k.clip_end_ms ?? k.duration_ms) - (k.clip_start_ms || 0))];
  const [multiSel, setMultiSel] = useState(null);
  const startMultiSelect = () => { setMultiSel(new Set(selectedTakeObj ? [selectedTakeObj.take_id] : [])); setRange(null); setRangeMode(false); };
  const copyMultiSelected = () => {
    const items = takes.filter((k) => multiSel?.has(k.take_id));
    if (!items.length) { toast.error("Tap clips to select them first"); return; }
    setClipboard({ group: items, origin: Math.min(...items.map((k) => k.beat_offset_ms || 0)) });
    setMultiSel(null);
    toast.success(`Copied ${items.length} clip${items.length > 1 ? "s" : ""} — move the playhead and tap Paste`);
  };
  const copyLayers = () => {
    if (!selectedTakeObj) return;
    const [ws, we] = range?.takeId === selectedTakeObj.take_id ? [range.startMs, range.endMs] : takeSpan(selectedTakeObj);
    const items = takes.filter((k) => { const [a, b] = takeSpan(k); return a < we && b > ws; });
    const origin = Math.min(...items.map((k) => takeSpan(k)[0]));
    setClipboard({ group: items, origin });
    setRange(null); setRangeMode(false);
    toast.success(`Copied ${items.length} layered clip${items.length > 1 ? "s" : ""} — move the playhead and tap Paste`);
  };
  const pasteGroup = async (group, origin) => {
    const at = Math.max(0, Math.round(playheadMs || 0));
    const allowed = group.filter((k) => canEditTake(k) && canWrite(k.track_id));
    const lanes = [...new Set(allowed.map((k) => k.track_id))];
    const before = Object.fromEntries(lanes.map((tid) => [tid, laneSnapshot(takesRef.current, tid)]));
    let ok = 0;
    let full = 0;
    for (const k of allowed) {
      try {
        await api.post(`${takeUrl(k.track_id, k.take_id)}/duplicate`, { target_track_id: k.track_id, beat_offset_ms: at + ((k.beat_offset_ms || 0) - origin) });
        ok += 1;
      } catch { full += 1; }
    }
    const data = await reload();
    if (ok) {
      const after = Object.fromEntries(lanes.map((tid) => [tid, laneSnapshot(data?.takes || [], tid)]));
      const restoreAll = async (snaps) => { for (const tid of lanes) await restoreLane(tid, snaps[tid]); };
      pushHistory({ label: "paste layers", undo: () => restoreAll(before), redo: () => restoreAll(after) });
    }
    const prot = group.length - allowed.length;
    toast.success(`Pasted ${ok} clip${ok === 1 ? "" : "s"}${prot ? ` · ${prot} skipped (protected lane)` : ""}${full ? ` · ${full} failed (lane full)` : ""}`);
  };
  const pasteClip = useCallback(async () => {
    const src = clipboard;
    if (src?.group) { await pasteGroup(src.group, src.origin); return; }
    const target = selectedTrackId && canWrite(selectedTrackId) ? selectedTrackId : null;
    if (!src) return;
    if (!target) { toast.error("Select one of your vocal lanes to paste into"); return; }
    const r = await laneEdit(target, "paste", () => api.post(`${takeUrl(src.track_id, src.take_id)}/duplicate`,
      { target_track_id: target, beat_offset_ms: Math.max(0, Math.round(playheadMs || 0)) }));
    if (r) { toast.success("Clip pasted"); setSelection({ kind: "vocal", trackId: target, takeId: r.data?.take?.take_id }); }
  }, [clipboard, selectedTrackId, playheadMs, laneEdit]); // eslint-disable-line

  const deleteSelectedTake = useCallback(async () => {
    const take = selectedTakeObj;
    if (!take) return;
    const r = await laneEdit(take.track_id, "delete", () => api.delete(takeUrl(take.track_id, take.take_id)));
    if (r) { setSelection(null); toast.message("Clip deleted · Undo to bring it back"); }
  }, [selectedTakeObj, laneEdit]); // eslint-disable-line

  const runRangeOp = useCallback(async (op) => {
    if (!range) return;
    const entries = rangeLaneEntries(takesRef.current, range, op);
    if (!entries) { toast.error("Select a longer section first"); return; }
    const label = { split: "split range", trim: "trim to range", delete: "delete range" }[op];
    const r = await laneEdit(range.trackId, label, () => api.put(`/studio/projects/${sessionId}/tracks/${range.trackId}/takes-state`, { takes: entries }));
    if (r) { setRange(null); setRangeMode(false); setSelection(null); toast.success(label.charAt(0).toUpperCase() + label.slice(1)); }
  }, [range, laneEdit, sessionId]);

  const moveTake = useCallback((take, offsetMs) => patchTake(take, { beat_offset_ms: Math.max(0, Math.round(offsetMs)) }, "move"), [patchTake]);

  const trimTake = useCallback(async (take, startMs, endMs) => {
    const cs0 = take.clip_start_ms || 0;
    const off0 = take.beat_offset_ms || 0;
    const s = Math.max(Math.round(startMs), cs0 - off0); // never before timeline 0
    const partial = { clip_start_ms: s, clip_end_ms: Math.round(endMs), beat_offset_ms: off0 + (s - cs0) };
    await patchTake(take, partial, "trim");
    setClipPreview(null);
  }, [patchTake]);

  const nudgeSelectedClip = useCallback(async (deltaMs) => {
    if (!selection) return;
    if (selection.kind === "vocal" && selectedTakeObj) await moveTake(selectedTakeObj, (selectedTakeObj.beat_offset_ms || 0) + deltaMs);
    else if (selection.kind === "beat") {
      const cs = Math.max(0, (beatRef.clip_start_ms || 0) + deltaMs);
      await editBeatClip({ clip_start_ms: cs, clip_end_ms: beatRef.clip_end_ms || beatDurationMs || (cs + 60000) }, "move beat");
    }
  }, [selection, selectedTakeObj, moveTake, editBeatClip, beatRef, beatDurationMs]);

  // Slider preview (visual + audible) and commit.
  const previewKey = selection?.kind === "vocal" ? selection.takeId : selection?.kind === "beat" ? "beat" : null;
  const previewClip = useCallback((partial) => {
    if (!previewKey) return;
    setClipPreview((p) => ({ key: previewKey, partial: { ...(p?.key === previewKey ? p.partial : {}), ...partial } }));
    if (typeof partial.clip_gain_db === "number") engineRef.current?.setClipGain(previewKey, partial.clip_gain_db);
  }, [previewKey]);
  const commitClip = useCallback(async (partial, label) => {
    if (selection?.kind === "vocal" && selectedTakeObj) await patchTake(selectedTakeObj, partial, label);
    else if (selection?.kind === "beat") await editBeatClip(partial, `beat ${label}`);
    setClipPreview(null);
  }, [selection, selectedTakeObj, patchTake, editBeatClip]);

  const normalizeSelectedTake = useCallback(() => {
    if (!selectedTakeObj) return;
    const db = StudioEngine.normalizeGainFromPeaks(clipPeaks[selectedTakeObj.clip_url]?.peaks || selectedTakeObj.peaks || [], 220);
    if (db <= 0) { toast.message("Clip is already near peak — no change."); return; }
    commitClip({ clip_gain_db: db }, "normalize");
    toast.success(`Normalized · +${db.toFixed(1)} dB`);
  }, [selectedTakeObj, clipPeaks, commitClip]);

  const commitTrimBeat = useCallback(async (startMs, endMs) => {
    await editBeatClip({ clip_start_ms: Math.round(startMs), clip_end_ms: Math.round(endMs) }, "trim beat");
    setDragPreview(null);
  }, [editBeatClip]);

  // ─── Playback / seek ────────────────────────────────────────────
  const playHandlers = useMemo(() => ({
    onTick: (s) => moveHead(s * 1000),
    onEnd: () => setPlayheadMs(0),
  }), [moveHead]);

  const doSeek = useCallback(async (ms) => {
    if (engineState === "recording") return;
    const clamped = Math.max(0, Math.min(sessionDurationMs, Math.round(ms)));
    setPlayheadMs(clamped);
    const eng = engineRef.current;
    if (eng?.state === "playing") eng.playFrom(clamped, { takes: takesRef.current, beatClip }, playHandlers);
  }, [engineState, sessionDurationMs, beatClip, playHandlers]);

  // Edits made while playing become audible straight away.
  useEffect(() => {
    const eng = engineRef.current;
    if (eng?.state === "playing") eng.playFrom(eng.positionMs(), { takes, beatClip }, playHandlers);
  }, [takes, beatClip]); // eslint-disable-line

  const doPlay = async () => {
    try {
      const eng = engineRef.current;
      if (engineState === "playing") { await eng?.pauseMix(); setPlayheadMs(eng?.positionMs() || playheadMs); return; }
      const ready = await ensureEngineReady();
      await ready.playFrom(playheadMs, { takes, beatClip }, playHandlers);
    } catch (e) { toast.error(e?.message || "Playback failed"); }
  };
  const doReturnToStart = async () => { await engineRef.current?.returnToStart(); setPlayheadMs(0); if (scrollRef.current) scrollRef.current.scrollLeft = 0; };
  const saveTitle = async () => {
    if (!isOwner) return;
    if (!title.trim() || title === project?.title) return;
    try { await api.patch(`/studio/projects/${sessionId}`, { title: title.trim() }); toast.success("Draft saved"); }
    catch { toast.error("Could not save title"); }
  };

  const selectedTrack = tracks.find((t) => t.track_id === selectedTrackId) || null;
  const selectedIndex = tracks.findIndex((t) => t.track_id === selectedTrackId);
  const patchLane = (partial) => selectedTrackId && patchTrack(selectedTrackId, partial);

  const takesByTrack = useMemo(() => {
    const m = new Map();
    for (const t of tracks) m.set(t.track_id, []);
    for (const k of takes) { if (!m.has(k.track_id)) m.set(k.track_id, []); m.get(k.track_id).push(k); }
    return m;
  }, [tracks, takes]);
  const isRecording = engineState === "recording";
  const isPlaying = engineState === "playing";

  const onClipTap = useCallback((take, ms) => {
    if (multiSel) {
      setMultiSel((s) => { const n = new Set(s); n.has(take.take_id) ? n.delete(take.take_id) : n.add(take.take_id); return n; });
      return;
    }
    setSelectedTrackId(take.track_id);
    if (selection?.takeId !== take.take_id) setRange(null);
    setSelection({ kind: "vocal", trackId: take.track_id, takeId: take.take_id });
    doSeek(ms);
  }, [doSeek, selection, multiSel]);
  const onTrimPreview = useCallback((take, partial) => setClipPreview({ key: take.take_id, partial }), []);
  const onRangeChange = useCallback((r) => setRange(r), []);

  const beatWaveformImgUrl = useMemo(() => (
    beatUrl && !beatView ? StudioEngine.cloudinaryWaveformUrl(beatUrl, { width: 2048, colorHex: "d946ef" }) : null
  ), [beatUrl, beatView]);
  const beatBrokenVisible = beatBroken && !beatView && !beatWaveformImgUrl;
  const beatWinStart = beatRef.clip_start_ms || 0;
  const beatWinEnd = beatRef.clip_end_ms || beatDurationMs || sessionDurationMs;
  const beatPreview = dragPreview?.kind === "beat" ? dragPreview : null;
  const beatShownStart = beatPreview ? beatPreview.startMs : beatWinStart;
  const beatShownEnd = beatPreview ? beatPreview.endMs : beatWinEnd;
  const beatLive = clipPreview?.key === "beat" ? { ...beatRef, ...clipPreview.partial } : beatRef;

  useEffect(() => {
    if (!project || searchParams.get("start") !== "beat") return;
    setSearchParams({}, { replace: true });
    if (!project.beat_source) openBeatPicker();
  }, [project]); // eslint-disable-line

  const [clipAnchor, setClipAnchor] = useState(null);
  useEffect(() => {
    if (!selection) { setClipAnchor(null); return undefined; }
    const sel = selection.kind === "beat" ? '[data-testid="lane-beat"]' : `[data-testid="take-clip-${selection.takeId}"]`;
    const place = () => {
      const el = document.querySelector(sel);
      if (!el) { setClipAnchor(null); return; }
      const r = el.getBoundingClientRect();
      const box = scrollRef.current?.getBoundingClientRect();
      const left = Math.max(box ? box.left : 0, r.left);
      setClipAnchor({ top: r.top, bottom: r.bottom, left });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => { window.removeEventListener("scroll", place, true); window.removeEventListener("resize", place); };
  }, [selection, takes, PX_PER_SEC]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center" data-testid="studio-session-loading">
        <p className="text-sm text-gray-400">Loading session…</p>
      </div>
    );
  }

  const clipToolbarVisible = !!selection && !isRecording;
  const canUndo = historyRef.current.undo.length > 0;
  const canRedo = historyRef.current.redo.length > 0;
  const lastUndoLabel = canUndo ? historyRef.current.undo[historyRef.current.undo.length - 1].label : null;
  const lastRedoLabel = canRedo ? historyRef.current.redo[historyRef.current.redo.length - 1].label : null;
  const toolsClip = selection?.kind === "vocal"
    ? (selectedTakeObj ? { ...selectedTakeObj, ...(clipPreview?.key === selectedTakeObj.take_id ? clipPreview.partial : {}) } : null)
    : beatLive;
  const toolsWin = toolsClip ? (toolsClip.clip_end_ms || toolsClip.duration_ms || beatDurationMs || 0) - (toolsClip.clip_start_ms || 0) : 0;
  const gridPx = (gridMs / 1000) * PX_PER_SEC;
  const timelineBg = snap ? { backgroundImage: `repeating-linear-gradient(to right, rgba(255,255,255,0.07) 0 1px, transparent 1px ${gridPx}px)` } : undefined;
  const liveDurMs = (liveRef.current.peaks.length / LIVE_RATE) * 1000;

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" data-testid="studio-session-page">
      <header className="flex items-center gap-2 px-3 h-12 border-b border-white/10 bg-[#0a0a10]">
        <button type="button" onClick={() => navigate("/studio")} className="text-fuchsia-300 hover:text-fuchsia-200 text-sm" data-testid="studio-back-btn">← Back</button>
        <Input value={title} readOnly={!isOwner} onChange={(e) => setTitle(e.target.value)} onBlur={saveTitle} placeholder="Untitled Session" className="flex-1 bg-transparent border-0 text-white text-sm font-bold focus-visible:ring-0 focus-visible:ring-offset-0" data-testid="studio-title-input" />
        {!isOwner && (
          <span className="text-[10px] font-bold px-2 py-1 rounded bg-emerald-500/15 text-emerald-200 truncate max-w-[40%]" data-testid="studio-shared-badge">
            Shared by {members[project?.user_id]?.name || "owner"}
          </span>
        )}
        {isOwner && <>
        <button type="button" onClick={openBeatPicker}
                className="text-[10px] font-bold px-2 py-1 rounded border border-fuchsia-500/40 text-fuchsia-200 hover:bg-fuchsia-500/10"
                data-testid="studio-add-replace-beat-btn">
          {project?.beat_source ? "🎧 Replace Beat" : "🎧 Add Beat"}
        </button>
        <button type="button" onClick={() => navigate(`/studio/project/${sessionId}#final-mix`)} disabled={engineState === "recording"}
                className="text-[10px] font-black px-2 py-1 rounded border border-amber-300/50 text-amber-200 hover:bg-amber-400/10 disabled:opacity-30"
                data-testid="studio-final-mix-btn">Final Mix</button>
        <Button onClick={saveTitle} className="bg-fuchsia-600 hover:bg-fuchsia-500 h-8 px-3 text-xs font-bold" data-testid="studio-save-draft-btn">💾</Button>
        </>}
      </header>

      {/* Edit bar — always-visible Undo / Redo / Snap */}
      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-white/5 bg-[#0a0a10]" data-testid="studio-edit-bar">
        <EditBtn onClick={runUndo} disabled={!canUndo || isRecording} testid="studio-undo-btn" title={lastUndoLabel ? `Undo ${lastUndoLabel}` : "Undo"}>
          <Undo2 className="w-4 h-4" /><span className="hidden sm:inline">Undo</span>
        </EditBtn>
        <EditBtn onClick={runRedo} disabled={!canRedo || isRecording} testid="studio-redo-btn" title={lastRedoLabel ? `Redo ${lastRedoLabel}` : "Redo"}>
          <Redo2 className="w-4 h-4" /><span className="hidden sm:inline">Redo</span>
        </EditBtn>
        <span className="text-[10px] text-gray-500 truncate flex-1" data-testid="studio-last-action">
          {lastUndoLabel ? `Last: ${lastUndoLabel}` : "Tap a clip to edit"}
        </span>
        {clipboard && (
          <EditBtn onClick={pasteClip} disabled={isRecording} testid="studio-paste-btn" title="Paste the copied clip at the playhead on the selected lane">
            <ClipboardPaste className="w-4 h-4" /><span>Paste</span>
          </EditBtn>
        )}
        <EditBtn onClick={() => setSnap((v) => !v)} active={snap} testid="studio-snap-toggle" title="Snap clips to the beat grid">
          <Magnet className="w-4 h-4" /><span>{snap ? (bpm ? `Snap ${Math.round(bpm)} BPM` : "Snap ¼s") : "Snap"}</span>
        </EditBtn>
      </div>

      <StudioToolsBar monitor={monitorOn} onMonitor={toggleMonitor} monitorVol={monitorVol} onMonitorVol={changeMonitorVol}
        metro={metro} onMetro={changeMetro} bpmFromBeat={!!bpm} zoom={ZOOMS[zoomIdx]} onZoom={zoomBy}
        zoomMin={ZOOMS[0]} zoomMax={ZOOMS[ZOOMS.length - 1]} />
      <main className={`flex-1 overflow-y-auto px-2 pt-2 ${clipToolbarVisible ? "pb-56" : "pb-28"}`} data-testid="studio-workspace">
        <LyricsPanel lyrics={lyrics} expanded={lyricsPanel} onToggle={toggleLyricsPanel}
          onEdit={isOwner ? () => setShowLyrics(true) : undefined} recording={engineState === "recording"} />
        <div ref={scrollRef} className="overflow-x-auto overflow-y-hidden bg-white/[0.01]"
             data-testid="studio-timeline-scroll" style={{ scrollbarWidth: "thin" }}>
          <div className="relative" style={{ width: totalPx, minWidth: "100%", ...timelineBg }} data-testid="studio-timeline-inner">
            <BarRuler durationMs={sessionDurationMs} pxPerSec={PX_PER_SEC} bpm={sessionBpm} onSeek={isRecording ? null : doSeek} />
            <PixelRuler durationMs={sessionDurationMs} pxPerSec={PX_PER_SEC} onSeek={isRecording ? null : doSeek} />

            {/* Beat lane */}
            <div className={`border-b border-white/[0.06] ${selection?.kind === "beat" ? "bg-amber-500/[0.06]" : ""}`} data-testid="lane-beat">
              <div className="flex items-center gap-2 px-2 py-1 sticky left-0 w-fit max-w-[90vw]">
                <span className={`inline-block w-2 h-2 rounded-full ${project?.beat_source ? "bg-fuchsia-500" : "bg-white/30"}`} />
                <p className="text-[11px] font-bold text-white/90 truncate">
                  {project?.beat_source ? `🎧 ${beatRef.title || (beatRef.mime || "").split("/").pop()?.toUpperCase() || "Beat"}${beatRef.producer_name ? ` · Prod. ${beatRef.producer_name}` : ""}` : "🎧 Beat — tap Add Beat above"}
                </p>
                <p className="text-[10px] text-gray-500" data-testid="beat-duration-label">
                  {project?.beat_source
                    ? (beatBrokenVisible ? "⚠ could not decode" : beatDurationMs ? formatSeconds(Math.round(beatDurationMs / 1000)) : "loading…")
                    : "No beat yet"}
                </p>
              </div>
              <div className="relative" style={{ height: LANE_HEIGHT }} data-testid="lane-beat-timeline"
                onClick={(e) => {
                  if (isRecording) return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  doSeek(((e.clientX - rect.left) / PX_PER_SEC) * 1000);
                  if (project?.beat_source && isOwner) { setSelection({ kind: "beat" }); setRange(null); }
                }}>
                {beatUrl && (
                  <div className="absolute inset-y-0" data-testid="beat-clip-window"
                       style={{ left: (beatShownStart / 1000) * PX_PER_SEC, width: Math.max(6, ((beatShownEnd - beatShownStart) / 1000) * PX_PER_SEC) }}>
                    <div className={`relative w-full h-full rounded ${selection?.kind === "beat" ? "ring-2 ring-amber-300/70" : ""}`}>
                      {beatView ? (
                        <WaveformCanvas peaks={beatView.peaks} rate={beatView.rate} srcStartMs={beatShownStart} srcEndMs={beatShownEnd}
                          pxPerSec={PX_PER_SEC} height={LANE_HEIGHT} color={selection?.kind === "beat" ? "#fbbf24" : "#d946ef"}
                          gainDb={beatLive.clip_gain_db || 0} fadeInMs={beatLive.fade_in_ms || 0} fadeOutMs={beatLive.fade_out_ms || 0}
                          showFades={selection?.kind === "beat"} testid="beat-wave-canvas" />
                      ) : (
                        <WaveformLane peaks={[]} imgSrc={beatWaveformImgUrl} empty={!beatWaveformImgUrl} height={LANE_HEIGHT} />
                      )}
                      {selection?.kind === "beat" && (
                        <>
                          <TrimHandle edge="start" pxPerSecond={PX_PER_SEC} clipStartMs={beatWinStart} clipEndMs={beatWinEnd} durationMs={beatDurationMs}
                            onLivePreview={(ms) => setDragPreview({ kind: "beat", startMs: ms, endMs: beatWinEnd })}
                            onCommit={(s, e) => commitTrimBeat(s, e)} />
                          <TrimHandle edge="end" pxPerSecond={PX_PER_SEC} clipStartMs={beatWinStart} clipEndMs={beatWinEnd} durationMs={beatDurationMs}
                            onLivePreview={(ms) => setDragPreview({ kind: "beat", startMs: beatWinStart, endMs: ms })}
                            onCommit={(s, e) => commitTrimBeat(s, e)} />
                        </>
                      )}
                    </div>
                  </div>
                )}
                {!beatUrl && <div className="absolute inset-0"><WaveformLane peaks={[]} empty height={LANE_HEIGHT} /></div>}
              </div>
            </div>

            {/* Vocal lanes */}
            {tracks.map((t, idx) => {
              const trackTakes = takesByTrack.get(t.track_id) || [];
              const laneTokens = laneColorTokens(t.color);
              const isSelected = selectedTrackId === t.track_id;
              const liveHere = isRecording && liveRef.current.trackId === t.track_id;
              const pendingHere = pendingTake?.trackId === t.track_id;
              return (
                <div key={t.track_id} className={`border-b border-white/[0.06] ${liveHere ? "bg-red-500/[0.08]" : isSelected ? laneTokens.tint : ""}`} data-testid={`lane-vocal-${idx}`} data-recording={liveHere ? "1" : "0"}>
                  <div className="sticky left-0 w-fit max-w-[100vw] flex items-center gap-1 h-0 overflow-visible relative z-[1]">
                    {liveHere && <span className="ml-1 text-[10px] font-black text-white bg-red-600 px-1.5 py-0.5 rounded animate-pulse" data-testid={`lane-vocal-${idx}-rec-badge`}>● REC</span>}
                    <button type="button" onClick={() => { setSelectedTrackId(t.track_id); setShowVocals(true); }} title={t.label}
                      className={`ml-0.5 mt-0.5 h-4 min-w-4 px-1 rounded-sm text-[9px] font-black leading-4 text-black ${isSelected ? "ring-1 ring-white" : ""} ${t.mute ? "line-through opacity-40" : isSelected ? "" : "opacity-80"}`}
                      style={{ background: `#${laneTokens.hex}` }} data-testid={`lane-vocal-${idx}-indicator`}>
                      {idx + 1}{t.solo ? " S" : ""}
                    </button>
                    {members[t.created_by || t.user_id] && (t.created_by || t.user_id) !== me && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/5 text-gray-400 truncate max-w-[120px]" data-testid={`lane-vocal-${idx}-artist`}>
                        by {members[t.created_by || t.user_id].name}
                      </span>
                    )}
                  </div>
                  <div className="relative cursor-pointer" style={{ height: LANE_HEIGHT }} data-testid={`lane-vocal-${idx}-timeline`}
                    onClick={(e) => {
                      if (isRecording) return;
                      const rect = e.currentTarget.getBoundingClientRect();
                      doSeek(((e.clientX - rect.left) / PX_PER_SEC) * 1000);
                      setSelectedTrackId(t.track_id);
                      setSelection(null); setRange(null);
                    }}>
                    {!liveHere && trackTakes.map((k) => {
                      const picked = (selection?.kind === "vocal" && selection.takeId === k.take_id) || !!multiSel?.has(k.take_id);
                      return (
                        <VocalClip key={k.take_id} take={k} colorHex={laneTokens.hex} pps={PX_PER_SEC} height={LANE_HEIGHT}
                          selected={picked} rangeMode={picked && rangeMode} range={picked && range?.takeId === k.take_id ? range : null}
                          peaksInfo={clipPeaks[k.clip_url]} preview={clipPreview?.key === k.take_id ? clipPreview.partial : null}
                          snap={snap ? gridMs : 0} disabled={isRecording || !canEditTake(k)}
                          onTap={onClipTap} onMoveCommit={moveTake} onTrimPreview={onTrimPreview} onTrimCommit={trimTake}
                          onRangeChange={onRangeChange} />
                      );
                    })}
                    {(liveHere || pendingHere) && (
                      <div className="absolute inset-y-0 rounded-md bg-red-500/10 border border-red-400/50 overflow-hidden"
                           style={{ left: ((liveHere ? liveRef.current.startMs : pendingTake.startMs) / 1000) * PX_PER_SEC,
                                    width: Math.max(4, ((liveHere ? liveDurMs : (pendingTake.peaks.length / pendingTake.rate) * 1000) / 1000) * PX_PER_SEC) }}
                           data-testid="live-take-region" data-live-buckets={liveHere ? liveVersion : pendingTake.peaks.length}>
                        <WaveformCanvas peaks={liveHere ? liveRef.current.peaks : pendingTake.peaks} rate={LIVE_RATE} version={liveHere ? liveVersion : -1}
                          srcStartMs={0} srcEndMs={liveHere ? liveDurMs : (pendingTake.peaks.length / pendingTake.rate) * 1000}
                          pxPerSec={PX_PER_SEC} height={LANE_HEIGHT} color="#f87171" testid="live-take-wave" />
                        {pendingHere && <span className="absolute right-1 top-1 text-[9px] font-bold text-red-200" data-testid="pending-take-label">Saving…</span>}
                      </div>
                    )}
                    {trackTakes.length === 0 && !liveHere && !pendingHere && (
                      <div className="absolute inset-0 pointer-events-none"><WaveformLane peaks={[]} empty color={t.color} height={LANE_HEIGHT} /></div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Single shared playhead — spans beat + every vocal lane */}
            <div ref={playheadLineRef} className="absolute top-0 bottom-0 left-0 w-[2px] bg-white shadow-[0_0_8px_rgba(255,255,255,0.6)] pointer-events-none z-[25] will-change-transform"
                 data-testid="studio-playhead" data-ms={playheadMs}>
              <div className="absolute -top-0.5 -left-[6px] w-0 h-0 border-l-[7px] border-r-[7px] border-t-[9px] border-l-transparent border-r-transparent border-t-white" />
            </div>
          </div>
        </div>



        {uploadProgress && (
          <div className="fixed inset-x-0 top-12 z-30 mx-auto max-w-md px-4" data-testid="studio-upload-progress">
            <div className="bg-[#0f0b1a] border border-fuchsia-500/40 rounded p-3">
              <p className="text-xs text-fuchsia-200 font-bold">{uploadProgress.note}</p>
              <div className="mt-2 h-2 rounded bg-white/10 overflow-hidden">
                <div className="h-full bg-fuchsia-500 transition-all" style={{ width: `${uploadProgress.pct}%` }} />
              </div>
              <p className="text-[10px] text-gray-500 mt-1 tabular-nums">{uploadProgress.pct}%</p>
            </div>
          </div>
        )}
      </main>

      <MixerDrawer track={selectedTrack} index={selectedIndex} open={showMixer}
                   onClose={() => setShowMixer(false)} onChange={patchTrack} />
      <LyricsDrawer open={showLyrics} value={lyrics} onClose={() => setShowLyrics(false)}
                    onSave={async (next) => { setLyrics(next); await saveLyrics(next); }} />
      <CollabDrawer open={showCollab} onClose={() => setShowCollab(false)} projectId={sessionId} isOwner={isOwner} tracks={tracks} />
      <FxDrawer open={showFx} onClose={() => setShowFx(false)} track={selectedTrack} studioPro={studioPro}
                previewLeft={previewLeft} onStartPreview={startFxPreview}
                onChange={(fx) => selectedTrackId && fxChange(selectedTrackId, fx)}
                onCommit={(fx) => selectedTrackId && fxCommit(selectedTrackId, fx)} />

      <ClipToolbar
        kind={selection?.kind || "vocal"} visible={clipToolbarVisible && !showTools && !multiSel} anchor={clipAnchor}
        onCopyLayers={selection?.kind === "vocal" ? copyLayers : undefined}
        onMultiSelect={selection?.kind === "vocal" ? startMultiSelect : undefined}
        onPaste={clipboard ? pasteClip : undefined}
        onMoveHere={selection?.kind === "vocal" && selectedTakeObj && canEditTake(selectedTakeObj) ? () => moveTake(selectedTakeObj, playheadMs || 0) : undefined}
        rangeMode={rangeMode} range={rangeMode ? range : null}
        onSplit={selection?.kind === "vocal" ? splitSelectedTake : undefined}
        onDuplicate={duplicateSelectedTake}
        onCopy={selection?.kind === "vocal" ? copySelectedTake : undefined}
        onDelete={selection?.kind === "vocal" && selectedTakeObj && takeAuthor(selectedTakeObj) === me ? deleteSelectedTake : undefined}
        onOpenTools={() => setShowTools(true)}
        onDeselect={() => { setSelection(null); setRange(null); setRangeMode(false); }}
        onToggleRange={() => { setRangeMode((v) => !v); setRange(null); }}
        onRangeSplit={() => runRangeOp("split")} onRangeTrim={() => runRangeOp("trim")}
        onRangeDelete={() => runRangeOp("delete")} onRangeClear={() => setRange(null)}
      />
      <ClipToolsDrawer
        open={showTools && !!toolsClip} onClose={() => { setShowTools(false); setClipPreview(null); }}
        kind={selection?.kind || "vocal"}
        gainDb={toolsClip?.clip_gain_db || 0} fadeInMs={toolsClip?.fade_in_ms || 0} fadeOutMs={toolsClip?.fade_out_ms || 0}
        maxFadeMs={toolsWin}
        onPreview={previewClip} onCommit={commitClip}
        onNormalize={selection?.kind === "vocal" ? normalizeSelectedTake : undefined}
        onNudge={nudgeSelectedClip}
        onUndo={runUndo} onRedo={runRedo} canUndo={canUndo} canRedo={canRedo} undoLabel={lastUndoLabel} redoLabel={lastRedoLabel}
      />

      <MasterDrawer open={showMaster} onClose={() => setShowMaster(false)} preset={master.preset}
        intensity={master.intensity} onChange={changeMaster} saving={masterSaving}
        volume={masterVolume} onVolume={changeMasterVolume}
        isOwner={isOwner} tracks={tracks} onTrack={patchTrack} canWrite={canWrite}
        beatVolume={beatVolume} onBeatVolume={(v) => { setBeatVolume(v); clearTimeout(beatVolTimer.current); if (isOwner) beatVolTimer.current = setTimeout(() => saveBeatVolume(v), 500); }}
        meter={<MasterMeter engineRef={engineRef} active={engineState === "playing" || engineState === "recording"} />} />
      {!isRecording && !multiSel && (
        <button type="button" onClick={async () => { await addVocalTrack(); setShowVocals(true); }}
          disabled={addingVocal || tracks.length >= MAX_VOCALS}
          className="fixed right-3 bottom-[68px] z-20 w-12 h-12 rounded-full bg-fuchsia-600 hover:bg-fuchsia-500 text-white shadow-[0_6px_20px_rgba(192,38,211,0.45)] flex items-center justify-center active:scale-95 transition-transform disabled:opacity-40"
          aria-label="Add vocal track" title="Add vocal track" data-testid="studio-add-vocal-btn">
          <Mic className="w-5 h-5" /><Plus className="w-3 h-3 absolute top-2 right-2" strokeWidth={3} />
        </button>
      )}
      <VocalTrackDrawer open={showVocals} onClose={() => setShowVocals(false)} tracks={tracks} max={MAX_VOCALS} highlightId={selectedTrackId}
        onAdd={() => addVocalTrack()} onPatch={patchTrack} canWrite={canWrite} busy={addingVocal}
        onFx={(trackId) => {
          if (!canWrite(trackId)) { toast.message("Only this lane's artist can change its effects"); return; }
          setSelectedTrackId(trackId); setShowVocals(false); setShowFx(true);
        }} />

      {multiSel && (
        <div className="fixed left-2 right-2 bottom-[64px] z-30 max-w-2xl mx-auto rounded-xl border border-fuchsia-400/40 bg-[#0f0b1a]/95 backdrop-blur flex items-center gap-2 px-3 py-2" data-testid="multi-select-bar">
          <span className="text-[11px] font-bold text-fuchsia-200 flex-1" data-testid="multi-select-count">{multiSel.size} clip{multiSel.size === 1 ? "" : "s"} selected · tap clips on any lane</span>
          <button type="button" onClick={copyMultiSelected} className="h-8 px-3 rounded-full bg-fuchsia-600 text-white text-[11px] font-bold" data-testid="multi-select-copy">Copy selected</button>
          <button type="button" onClick={() => setMultiSel(null)} className="h-8 px-3 rounded-full border border-white/20 text-[11px] text-gray-200" data-testid="multi-select-cancel">Cancel</button>
        </div>
      )}
      {/* BOTTOM TRANSPORT */}
      <nav className="fixed bottom-0 left-0 right-0 z-20 bg-[#0a0a10] border-t border-white/10" data-testid="studio-transport">
        {showBeatVol && (
          <BeatVolumeSlider value={beatVolume} onChange={setBeatVolume} onCommit={isOwner ? saveBeatVolume : () => {}} onClose={() => setShowBeatVol(false)} />
        )}
        <div className="max-w-2xl mx-auto flex items-center justify-between px-3 py-1">
          <button type="button" onClick={() => setShowBeatVol((v) => !v)} className={`text-lg ${showBeatVol ? "text-fuchsia-300" : "text-gray-300 hover:text-white"}`} data-testid="studio-volume-btn" title="Beat volume">
            {beatVolume < 0.4 ? "🔈" : beatVolume < 0.9 ? "🔉" : "🔊"}
          </button>
          <button type="button" onClick={doReturnToStart} disabled={isRecording} className="text-gray-300 hover:text-white text-xl disabled:opacity-30" data-testid="studio-return-btn">⏮</button>
          <button type="button" onClick={doPlay} disabled={!project?.beat_source || isRecording} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-lg disabled:opacity-30" data-testid="studio-play-btn">{isPlaying ? "⏸" : "▶"}</button>
          {!isRecording ? (
            <button type="button" onClick={doRecord} disabled={!project?.beat_source || uploading} className="w-11 h-11 rounded-full bg-red-600 hover:bg-red-500 flex items-center justify-center shadow-[0_0_20px_rgba(239,68,68,0.5)] disabled:opacity-30 disabled:shadow-none" data-testid="studio-record-btn"><span className="w-5 h-5 rounded-full bg-white" /></button>
          ) : (
            <button type="button" onClick={doStopRecord} className="w-11 h-11 rounded-full bg-white flex items-center justify-center animate-pulse" data-testid="studio-stop-btn"><span className="w-5 h-5 bg-black rounded-sm" /></button>
          )}
          <button type="button" onClick={() => setShowMixer((v) => !v)} disabled={!selectedTrack} className={`text-gray-300 hover:text-white text-lg disabled:opacity-30 ${showMixer ? "text-fuchsia-300" : ""}`} data-testid="studio-mixer-toggle-btn">🎚️</button>
          {<button type="button" onClick={() => setShowMaster((v) => !v)} className={`text-[10px] font-black tracking-wider px-1.5 py-1 rounded border ${master.preset !== "off" ? "border-amber-300 bg-amber-400 text-black" : "border-amber-400/40 text-amber-300"}`} data-testid="studio-master-toggle-btn" title="Master / Mix">MIX</button>}
          <button type="button" onClick={toggleLyricsPanel} className={`text-gray-300 hover:text-white text-lg ${lyricsPanel ? "text-fuchsia-300" : ""}`} data-testid="studio-lyrics-toggle-btn" aria-pressed={lyricsPanel} title="Show / hide lyrics">📝</button>
          <button type="button" onClick={() => setShowCollab((v) => !v)} className={`text-gray-300 hover:text-white text-lg ${showCollab ? "text-fuchsia-300" : ""}`} data-testid="studio-collab-toggle-btn">👥</button>
          <span className="text-[10px] text-gray-400 tabular-nums w-12 text-right" data-testid="studio-time">{formatSeconds(Math.floor(playheadMs / 1000))}.{Math.floor((playheadMs % 1000) / 100)}</span>
        </div>
        {isRecording && (
          <div className="max-w-2xl mx-auto flex items-center justify-between gap-3 px-4 pb-2 -mt-1" data-testid="studio-input-meter-row">
            <span className="text-[10px] uppercase tracking-widest text-red-400 font-bold">● Rec {formatSeconds(Math.floor(liveDurMs / 1000))}</span>
            <InputMeter level={micLevel} color={selectedTrack?.color || "fuchsia"} />
            <span className="text-[10px] tabular-nums text-gray-500 w-10 text-right" data-testid="mic-input-level-text">{Math.round((micLevel / 255) * 100)}%</span>
          </div>
        )}
      </nav>


      {showBeatPicker && (
        <div className="fixed inset-0 z-40 bg-black/70 flex items-end sm:items-center justify-center p-4" data-testid="studio-beat-picker">
          <div className="max-w-md w-full p-4 rounded-lg bg-[#0f0b1a] border border-fuchsia-500/40 max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-black">Pick a Beat</h3>
            <p className="text-xs text-gray-400 mt-1">Free Marketplace beats, beats you've licensed, or your own upload. Producer credit stays with every beat.</p>
            <div className="mt-3">
              <p className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">My Licensed Beats</p>
              {purchases === null && <p className="text-xs text-gray-500 mt-2">Loading…</p>}
              {purchases && purchases.length === 0 && (
                <p className="text-xs text-gray-500 mt-2" data-testid="studio-no-licensed">
                  You haven't licensed any Marketplace beats yet.{" "}
                  <button className="text-fuchsia-300 underline" onClick={() => navigate("/beats")}>Browse the Marketplace →</button>
                </p>
              )}
              {purchases && purchases.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {purchases.map((p) => (
                    <li key={p.beat_id}>
                      <button onClick={() => attachMarketplaceBeat(p.beat_id)} className="w-full text-left p-2 rounded bg-black/30 border border-white/10 hover:border-fuchsia-400/40 text-sm flex items-center gap-2" data-testid={`studio-pick-marketplace-${p.beat_id}`}>
                        {p.beat?.cover_url && <img src={p.beat.cover_url} alt="" className="w-8 h-8 rounded object-cover" />}
                        <span className="flex-1 truncate">
                          <span className="font-semibold">{p.beat?.title || "Beat"}</span>
                          <span className="text-[10px] text-gray-500 block">{p.tier_label || p.license_type}</span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="mt-4">
              <p className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">Free Marketplace Beats</p>
              {freeBeats === null && <p className="text-xs text-gray-500 mt-2">Loading…</p>}
              {freeBeats && freeBeats.length === 0 && <p className="text-xs text-gray-500 mt-2" data-testid="studio-no-free-beats">No free beats available right now.</p>}
              {freeBeats && freeBeats.length > 0 && (
                <ul className="mt-2 space-y-1 max-h-56 overflow-y-auto" data-testid="studio-free-beats">
                  {freeBeats.map((b) => (
                    <li key={b.beat_id}>
                      <button onClick={() => attachMarketplaceBeat(b.beat_id)} className="w-full text-left p-2 rounded bg-black/30 border border-white/10 hover:border-emerald-400/40 text-sm flex items-center gap-2" data-testid={`studio-pick-free-${b.beat_id}`}>
                        {b.cover_url && <img src={b.cover_url} alt="" className="w-8 h-8 rounded object-cover" />}
                        <span className="flex-1 truncate">
                          <span className="font-semibold">{b.title || "Beat"}</span>
                          <span className="text-[10px] text-gray-500 block">Prod. {b.producer_name || "RGM producer"}{b.bpm ? ` · ${b.bpm} BPM` : ""} · Free</span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="mt-4">
              <p className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">Or Upload Your Own</p>
              <label className="mt-2 block">
                <span className="sr-only">Upload beat</span>
                <input type="file" accept={BEAT_ACCEPT} onChange={(e) => uploadBeatFile(e.target.files?.[0])} disabled={uploading} className="text-xs text-gray-300" data-testid="studio-upload-beat-input" />
              </label>
              <p className="text-[10px] text-gray-500 mt-1">MP3, WAV, M4A, AAC, OGG or WebM. Up to {humanBytes(BEAT_HARD_CAP)}.</p>
            </div>
            <div className="mt-4 flex justify-end">
              <Button variant="outline" onClick={() => setShowBeatPicker(false)} className="border-white/20" data-testid="studio-beat-picker-close">Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EditBtn({ onClick, disabled, active, testid, title, children }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title} aria-label={title} data-testid={testid} aria-pressed={active}
      className={`flex items-center gap-1 px-2.5 h-8 rounded-full text-[11px] font-bold border transition-colors disabled:opacity-30 ${
        active ? "border-fuchsia-400 bg-fuchsia-500/20 text-fuchsia-100" : "border-white/15 text-gray-200 hover:bg-white/5"}`}>
      {children}
    </button>
  );
}

// ─── Pixel-based ruler ─────────────────────────────────────────────
// Musical bars (4/4) at the session BPM; bar 1 starts at timeline 0.
function BarRuler({ durationMs, pxPerSec, bpm, onSeek }) {
  const barSec = (60 / bpm) * 4;
  const barPx = barSec * pxPerSec;
  const bars = Math.ceil(Math.max(1000, durationMs || 60000) / 1000 / barSec) + 1;
  const every = barPx >= 28 ? 1 : barPx >= 14 ? 2 : barPx >= 7 ? 4 : 8;
  const seek = (e) => { if (!onSeek) return; const r = e.currentTarget.getBoundingClientRect(); onSeek(((e.clientX - r.left) / pxPerSec) * 1000); };
  return (
    <div className="relative h-5 select-none bg-white/[0.02] overflow-hidden" style={{ width: Math.ceil(Math.max(1000, durationMs || 60000) / 1000 * pxPerSec) }} onPointerDown={seek}
      data-testid="timeline-bar-ruler" data-bpm={bpm}>
      {Array.from({ length: bars }, (_, i) => (
        <div key={i} className={`absolute top-0 bottom-0 border-l ${i % 4 === 0 ? "border-white/25" : "border-white/10"}`} style={{ left: i * barPx }}>
          {i % every === 0 && <span className="pl-1 text-[10px] font-bold text-amber-200/90 tabular-nums" data-testid={`bar-label-${i + 1}`}>{i + 1}</span>}
        </div>
      ))}
    </div>
  );
}

function PixelRuler({ durationMs, pxPerSec, onSeek }) {
  const dur = Math.max(1000, durationMs || 60000);
  const totalSec = Math.ceil(dur / 1000);
  const step = totalSec <= 30 ? 5 : totalSec <= 120 ? 10 : 30;
  const ticks = [];
  for (let s = 0; s <= totalSec; s += step) ticks.push(s);
  const totalPx = Math.ceil(totalSec * pxPerSec);
  return (
    <div
      className={`relative h-8 text-[10px] text-gray-400 select-none border-b border-white/10 ${onSeek ? "cursor-pointer" : ""}`}
      // Tap or drag to place the playhead (record / punch-in position).
      style={{ width: totalPx, touchAction: "none" }}
      onPointerDown={(e) => {
        if (!onSeek) return;
        e.currentTarget.setPointerCapture?.(e.pointerId);
        const rect = e.currentTarget.getBoundingClientRect();
        onSeek(((e.clientX - rect.left) / pxPerSec) * 1000);
      }}
      onPointerMove={(e) => {
        if (!onSeek || !e.currentTarget.hasPointerCapture?.(e.pointerId)) return;
        const rect = e.currentTarget.getBoundingClientRect();
        onSeek(((e.clientX - rect.left) / pxPerSec) * 1000);
      }}
      data-testid="timeline-ruler"
    >
      {ticks.map((s) => (
        <div key={s} className="absolute top-0 bottom-0 border-l border-white/10 pl-1"
             style={{ left: s * pxPerSec }}>
          {s === 0 ? null : <span>{formatSeconds(s)}</span>}
        </div>
      ))}
    </div>
  );
}
